import React, { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams, Navigate } from 'react-router-dom';
import SystemsHeader from '../../components/SystemsHeader';
import SystemsFooter from '../../components/SystemsFooter';
import SeoHead from '../../components/SeoHead';
import { useSystemsCatalog } from '../../context/SystemsCatalogContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getBreadcrumbSchema } from '../../data/breadcrumbSchema';
import { Check, ArrowLeft, Sparkles } from 'lucide-react';

export default function ServicePricing() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const highlight = params.get('highlight');
  const fromGetStarted = params.get('from') === 'get-started';
  const highlightRef = useRef(null);
  const { getService, loading } = useSystemsCatalog();
  const { format } = useCurrency();

  useEffect(() => {
    if (!highlight) return;
    const timer = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [highlight, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SystemsHeader />
        <main className="py-32 text-center text-gray-500">Loading pricing…</main>
        <SystemsFooter />
      </div>
    );
  }
  const svc = getService(slug);
  if (!svc) return <Navigate to="/systems/solutions" replace />;

  const subscribeLabel = svc.billing === 'subscription' ? 'Subscribe Now' : 'Buy Now';

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        path={`/systems/solutions/${slug}/pricing`}
        title={`${svc.name} Pricing — ZHELTA Systems`}
        description={`Transparent pricing for ${svc.name}. Choose the plan that fits your business.`}
        jsonLd={getBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Systems', path: '/systems' },
          { name: 'Solutions', path: '/systems/solutions' },
          { name: svc.name, path: `/systems/solutions/${slug}` },
          { name: 'Pricing', path: `/systems/solutions/${slug}/pricing` },
        ])}
      />
      <SystemsHeader />
      <main className="py-20 md:py-28">
        <div className="zh-container max-w-6xl">
          {/* Header */}
          <div className="text-center mb-14">
            {fromGetStarted && (
              <div
                className="mb-8 inline-flex items-center gap-2 rounded-full bg-[#FEFBF3] border border-amber-100 px-5 py-2"
                data-testid="pricing-from-getstarted-banner"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#c9a227]" strokeWidth={2.2} />
                <span className="text-[12px] font-semibold text-[#c9a227] uppercase tracking-[0.14em]">Recommended based on your setup</span>
              </div>
            )}
            <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#c9a227]">
              {svc.billing === 'subscription' ? 'Standalone Solution' : 'Standalone Solution — One-Time Project'}
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
              {svc.name} <span className="text-[#f5a623]">Pricing</span>
            </h1>
            <p className="mt-6 text-[17px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {svc.description}
            </p>
            <p className="mt-3 text-[13px] text-gray-500">
              Looking for an all-inclusive plan? Check out our{' '}
              <Link to="/systems/solutions/managed-it-services/pricing" className="text-[#c9a227] font-semibold hover:text-[#f5a623]">Managed IT Service Plans</Link>
            </p>
          </div>

          {/* Tier cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {svc.tiers.map((tier) => {
              const popular = tier.popular;
              const isRecommended = highlight === tier.id;
              return (
                <div
                  key={tier.id}
                  ref={isRecommended ? highlightRef : undefined}
                  data-testid={`tier-${tier.id}`}
                  className={`relative rounded-2xl bg-white p-7 flex flex-col transition-all ${
                    isRecommended
                      ? 'border-2 border-[#f5a623] shadow-[0_0_0_6px_rgba(245,166,35,0.18),0_16px_48px_-16px_rgba(245,166,35,0.6)] md:scale-[1.05] ring-2 ring-amber-100'
                      : popular
                      ? 'border-2 border-[#f5a623] shadow-[0_16px_48px_-16px_rgba(245,166,35,0.5)] md:scale-[1.03]'
                      : 'border border-gray-200'
                  }`}
                >
                  {isRecommended && (
                    <span className="absolute -top-3 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#c9a227] text-white text-[10px] font-extrabold tracking-[0.14em] shadow-lg" data-testid={`tier-${tier.id}-recommended-badge`}>
                      <Sparkles className="w-2.5 h-2.5" strokeWidth={2.5} /> RECOMMENDED FOR YOU
                    </span>
                  )}
                  {popular && !isRecommended && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#f5a623] text-black text-[10px] font-extrabold tracking-[0.18em]">MOST POPULAR</span>
                  )}
                  <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
                  <p className="mt-2 text-[14px] text-gray-600 leading-relaxed min-h-[2.5rem]">{tier.description}</p>
                  <div className="mt-5 flex items-end gap-1">
                    <span className="text-4xl font-extrabold text-gray-900 tracking-tight">{format(Math.round(tier.price))}</span>
                    <span className="text-[13px] text-gray-500 mb-1.5">/{svc.unit}</span>
                  </div>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-[14px] text-gray-700">
                        <Check className="w-4 h-4 text-[#f5a623] mt-0.5 flex-shrink-0" strokeWidth={3} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 space-y-2">
                    <Link
                      to={`/systems/payment?slug=${slug}&tier=${tier.id}`}
                      data-testid={`tier-${tier.id}-subscribe`}
                      className={`block text-center px-5 py-3 rounded-full text-[13px] font-semibold uppercase tracking-wider transition ${
                        isRecommended ? 'bg-[#f5a623] text-black hover:brightness-110 animate-pulse-slow'
                        : popular ? 'bg-[#f5a623] text-[#0e0e10] hover:brightness-105 shadow-lg' : 'bg-[#f5a623] text-black hover:brightness-110'
                      }`}
                    >
                      {subscribeLabel}
                    </Link>
                    <Link to={`/systems/solutions/${slug}`} className="block text-center text-[13px] font-semibold uppercase tracking-wider text-gray-600 hover:text-black py-2 transition">Learn More</Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Extras */}
          <div className="mt-14 flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-gray-100">
            <Link to={`/systems/custom-build/${slug}`} data-testid="custom-build-link" className="text-[14px] font-semibold text-gray-700 hover:text-[#f5a623] transition">
              Need something different? <span className="underline underline-offset-4">Custom Build</span>
            </Link>
            <Link to="/systems/pricing" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-gray-600 hover:text-black transition">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to All Pricing
            </Link>
          </div>
        </div>
      </main>
      <SystemsFooter />
    </div>
  );
}
